import { Plural, Trans } from '@lingui/react/macro';
import { useTaskProgress } from '@/api/hooks';
import { Badge } from '@/components/ui/badge';
import type { SimulationResultBasicInfo, SimulationState } from '@/types/api';
import { cn } from '@/utils/cn';
import { stateBadgeClass } from '@/utils/simulationState';

interface SimulationStateBadgeProps {
  state: SimulationState;
  simulationResult?: SimulationResultBasicInfo | null;
  className?: string;
}

/**
 * Displays a colored badge for the current simulation state.
 * Shows collision count when state is complete_collisions.
 * Shows progress percentage when state is running and background_task_id is available.
 */
export const SimulationStateBadge = ({
  state,
  simulationResult,
  className,
}: SimulationStateBadgeProps): React.ReactNode => {
  const progress = useTaskProgress(simulationResult?.background_task_id ?? null);

  const label = (): React.ReactNode => {
    switch (state) {
      case 'complete_ok':
        return <Trans>No collisions</Trans>;
      case 'complete_collisions': {
        const count = simulationResult?.total_collision_count ?? 0;
        return <Plural value={count} one="# collision" other="# collisions" />;
      }
      case 'running':
        if (progress?.progress_percent != null) {
          return <Trans>Running…</Trans>;
        }
        return <Trans>Running…</Trans>;
      case 'error':
        return <Trans>Error</Trans>;
      case 'ready':
        return <Trans>Ready</Trans>;
      case 'incomplete':
      default:
        return <Trans>Incomplete</Trans>;
    }
  };

  const percent = progress?.progress_percent;

  return (
    <Badge className={cn(stateBadgeClass(state), className)}>
      {label()}
      {state === 'running' && percent != null && <span className="tabular-nums">{percent}%</span>}
    </Badge>
  );
};
